import React, { useEffect, useState } from 'react';
import { Assessment } from '@exerai/vision-engine-web';
import { Box, IconButton, Typography } from '@mui/material';
import { AssessmentOutcome } from '@/components/Scan/Outcomes/types/types';
import { EditIcon } from '@/components/common/Icons/EditIcon';
import { SlideUpTransition } from '@/components/common/Transitions/SlideTransition';
import { useAdminOutcomesContext } from '@/components/context/hooks/AdminOutcomesContext';
import { useAssessmentsContext } from '@/components/context/hooks/AssessmentsContext';
import { AssessmentCardContainer } from '../Assessments/styles';
import { AssessmentOutcomePreview } from './AssessmentOutcomePreview';
import { OutcomeBuilder } from './OutcomeBuilder/OutcomeBuilder';
import { OutcomeBuilderProvider } from './OutcomeBuilder/state/OutcomeBuilderContext';
import defaultOutcomes from './default.json';
import { Dialog, DialogContent, DialogTitle, OutcomeBuilderModalWrapper, OutcomeContainer } from './styles';

interface OutcomeModalProps {
    open: boolean;
    assessmentId: string;
    onClose: () => void;
}

export const OutcomeModals = (props: OutcomeModalProps) => {
    const { open, onClose, assessmentId } = props;
    const { assessmentsDict } = useAssessmentsContext();
    const { outcomes, organizationId } = useAdminOutcomesContext();
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [outcome, setOutcome] = useState<AssessmentOutcome>();
    const [assessment, setAssessment] = useState<Assessment>();
    const defaultOutcome = (defaultOutcomes as AssessmentOutcome[]).find((o) => o.assessmentId === assessmentId);
    const [isNew, setIsNew] = useState<boolean>(false);

    const close = () => {
        setIsEditing(false);
        setIsNew(false);
        onClose();
    };

    useEffect(() => {
        if (assessmentId && assessmentsDict && outcomes && open) {
            setAssessment(assessmentsDict[assessmentId]);
            const orgOutcome = outcomes?.find((o) => o.assessmentId === assessmentId);

            if (!orgOutcome) {
                // If outcome has not yet been configured for the organization, preload a default configuration to be edited
                setOutcome(defaultOutcome);
                setIsEditing(true);
                setIsNew(true);
            } else {
                //  setIsNew(false);
                setOutcome(orgOutcome);
            }
        }
    }, [assessmentId, outcomes, assessmentsDict, defaultOutcome, open]);

    if (!outcome || !assessment || !organizationId) {
        return null;
    }
    return (
        <>
            <Dialog open={open} onClose={close} maxWidth={false}>
                <DialogTitle>
                    <>
                        Analysis
                        <IconButton
                            edge="end"
                            aria-label="edit"
                            onClick={() => {
                                setIsEditing(true);
                            }}
                            style={{ marginLeft: 16 }}
                        >
                            <EditIcon />
                        </IconButton>
                    </>
                </DialogTitle>
                <DialogContent style={{ fontSize: 'small' }}>
                    <OutcomeContainer>
                        <AssessmentCardContainer>
                            <Box
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                {assessment.thumbnailUrl && (
                                    <Box
                                        style={{
                                            marginRight: 8,
                                        }}
                                    >
                                        <img src={assessment.thumbnailUrl} width={50} height={50} />
                                    </Box>
                                )}
                                <Box
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'flex-start',
                                        alignItems: 'flex-start',
                                        flexDirection: 'column',
                                    }}
                                >
                                    <Typography variant="h5">{assessment.name}</Typography>
                                    <Typography variant="caption">({assessment.subjectOrientation})</Typography>
                                </Box>
                            </Box>
                        </AssessmentCardContainer>
                        <AssessmentOutcomePreview outcome={outcome} assessment={assessment} />
                    </OutcomeContainer>
                </DialogContent>
            </Dialog>
            <OutcomeBuilderModalWrapper open={isEditing} TransitionComponent={SlideUpTransition}>
                {' '}
                <OutcomeBuilderProvider
                    outcome={outcome}
                    assessment={assessment}
                    organizationId={organizationId}
                    close={close}
                    isNew={isNew}
                >
                    <OutcomeBuilder closeModal={close} />
                </OutcomeBuilderProvider>
            </OutcomeBuilderModalWrapper>
        </>
    );
};
